import React, { useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { BillingPeriodSelector } from './BillingPeriodSelector';
import {
  ServiceOrder,
  CreateServiceOrderRequest,
  UpdateServiceOrderRequest
} from '../../types/serviceOrder';

interface ServiceOrderFormProps {
  serviceOrder?: ServiceOrder | null;
  onSubmit: (data: CreateServiceOrderRequest | UpdateServiceOrderRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const ServiceOrderForm: React.FC<ServiceOrderFormProps> = ({ 
  serviceOrder, 
  onSubmit, 
  onCancel, 
  loading = false 
}) => {
  // Calculate default billing period (always current month for new OS)
  const getDefaultBillingPeriod = () => {
    const now = new Date();
    return { month: now.getMonth() + 1, year: now.getFullYear() };
  };

  // Initialize billing period
  const getInitialBilling = () => {
    if (serviceOrder) {
      return {
        month: serviceOrder.billingMonth || getDefaultBillingPeriod().month,
        year: serviceOrder.billingYear || getDefaultBillingPeriod().year
      };
    }
    return getDefaultBillingPeriod();
  };

  const [formData, setFormData] = useState({
    name: serviceOrder?.name || '',
    description: serviceOrder?.description || '',
    startDate: serviceOrder?.startDate ? serviceOrder.startDate.split('T')[0] : '',
    endDate: serviceOrder?.endDate ? serviceOrder.endDate.split('T')[0] : '',
    billingMonth: getInitialBilling().month,
    billingYear: getInitialBilling().year
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    } else if (formData.name.length > 200) {
      newErrors.name = 'Nome deve ter no máximo 200 caracteres';
    }

    if (formData.description.length > 500) {
      newErrors.description = 'Descrição deve ter no máximo 500 caracteres';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Data de início é obrigatória';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'Data de fim é obrigatória';
    }

    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      
      if (endDate <= startDate) {
        newErrors.endDate = 'Data de fim deve ser posterior à data de início';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Evita regressão de 1 dia por fuso horário ao salvar datas sem hora
      const toNoonUTC = (dateStr: string) => {
        const [y, m, d] = dateStr.split('-').map(Number);
        // Usa meio‑dia UTC para manter o mesmo dia em fusos negativos
        return new Date(Date.UTC(y, (m || 1) - 1, d || 1, 12, 0, 0)).toISOString();
      };

      const submitData = {
        name: formData.name,
        description: formData.description,
        startDate: toNoonUTC(formData.startDate),
        endDate: toNoonUTC(formData.endDate),
        billingMonth: formData.billingMonth,
        billingYear: formData.billingYear
      };

      await onSubmit(submitData);
    } catch (error) {
      console.error('Erro ao salvar ordem de serviço:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <Card variant="glass" className="w-full max-w-2xl max-h-[90vh] overflow-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl text-foreground-primary">
            {serviceOrder ? 'Editar Ordem de Serviço' : 'Nova Ordem de Serviço'}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={loading}
            className="text-foreground-tertiary hover:text-foreground-primary"
          >
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-foreground-secondary">
                Nome da Ordem de Serviço *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Digite o nome da ordem de serviço"
                className={errors.name ? 'border-red-500' : ''}
                disabled={loading}
              />
              {errors.name && (
                <p className="text-sm text-red-400">{errors.name}</p>
              )}
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-foreground-secondary">
                Descrição
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(value) => handleInputChange('description', value)}
                placeholder="Descreva a ordem de serviço (opcional)"
                rows={3}
                className={errors.description ? 'border-red-500' : ''}
                disabled={loading}
              />
              {errors.description && (
                <p className="text-sm text-red-400">{errors.description}</p>
              )}
            </div>

            {/* Grid com 2 colunas para datas */}
            <div className="grid grid-cols-2 gap-4">
              {/* Data de Início */}
              <div className="space-y-2">
                <Label htmlFor="startDate" className="text-foreground-secondary">
                  Data de Início *
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  className={errors.startDate ? 'border-red-500' : ''}
                  disabled={loading}
                />
                {errors.startDate && (
                  <p className="text-sm text-red-400">{errors.startDate}</p>
                )}
              </div>

              {/* Data de Fim */}
              <div className="space-y-2">
                <Label htmlFor="endDate" className="text-foreground-secondary">
                  Data de Fim *
                </Label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  className={errors.endDate ? 'border-red-500' : ''}
                  disabled={loading}
                />
                {errors.endDate && (
                  <p className="text-sm text-red-400">{errors.endDate}</p>
                )}
              </div>
            </div>

            {/* Billing Period Selector */}
            <BillingPeriodSelector
              month={formData.billingMonth}
              year={formData.billingYear}
              startDate={formData.startDate}
              onChange={(month, year) => {
                setFormData(prev => ({ ...prev, billingMonth: month, billingYear: year }));
              }}
            />

            {/* Botões */}
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                variant="gradient"
                loading={loading}
                loadingText="Salvando..."
                className="flex-1"
              >
                <Save className="w-4 h-4 mr-2" />
                {serviceOrder ? 'Atualizar' : 'Criar'} Ordem de Serviço
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={onCancel}
                disabled={loading}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
